import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Field, Form, Formik, FormikConfig } from 'formik';
import { useTranslation } from 'react-i18next';

import { Button } from 'common/components/Button';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import {
    HEADER_IMAGE_MAX_SIZE,
    HEADER_IMAGE_TYPE,
} from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import {
    ColorPicker,
    ImageField,
    TextField,
} from 'common/components/FormField';
import { IUpdatePortfolioSettingsInput } from 'graphql/types';

interface IProps extends FormikConfig<IUpdatePortfolioSettingsInput> {
    disabled?: boolean;
    isDeleting?: boolean;
    onClose?(): void;
}

export const PortfolioSettingsForm = ({
    disabled,
    isDeleting,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    return (
        <Formik enableReinitialize {...other}>
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: 4 }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ fontWeight: 'bold' }} variant="h3">
                            {translate('portfolioSettingsDrawer.title')}
                        </Typography>

                        <Box sx={{ mt: 1 }}>
                            <Typography>
                                {translate('portfolioSettingsDrawer.text')}
                            </Typography>
                        </Box>
                    </Box>

                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting && !isDeleting}
                        onConfirm={submitForm}
                    />

                    <Form>
                        <Field
                            withDefaults
                            acceptedFileTypes={HEADER_IMAGE_TYPE}
                            component={ImageField}
                            helperText={translate('imageSizeDescription', {
                                width: IMAGE_VARIANTS[EImageVariant.Header]
                                    .minWidth,
                                height: IMAGE_VARIANTS[EImageVariant.Header]
                                    .minHeight,
                            })}
                            imageVariant={EImageVariant.Header}
                            label={translate('headerImage')}
                            maxUploadSize={HEADER_IMAGE_MAX_SIZE}
                            name="image"
                        />

                        <Box>
                            <Grid container spacing={2}>
                                <Grid size="grow">
                                    <Field
                                        component={TextField}
                                        label={translate('title')}
                                        name="title"
                                    />
                                </Grid>

                                <Grid>
                                    <Field
                                        component={ColorPicker}
                                        name="titleColor"
                                        popoverPosition="bottom-end"
                                        tooltip={translate('titleColor')}
                                    />
                                </Grid>
                            </Grid>

                            <Field
                                multiline
                                component={TextField}
                                label={translate('description')}
                                name="description"
                            />
                        </Box>

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    disabled ||
                                    (!isSubmitting && !disabled && !dirty)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>
                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
